import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import { EntityInteractionEventType } from '../selection';
import * as THREE from 'three/webgpu';
import {
    isSketchArcObject,
    isSketchLineObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
    isSketchCollectionObject,
    isSketchSlotObject,
    type AbstractSketchEdgeObject,
    SketchLineObject,
    type SketchPointObject,
} from '../../mesh/sketch';
import { isSketchContour, type SketchContour } from '../../mesh';
import { isAbstractSketchEdgeObject, isSketchPointObject } from '../../mesh/guards';
import type { EntitySelectionManagerRuntime } from '../selection/EntitySelectionRuntime';
import type {EntitySelectionMode} from '../selection/EntitySelectionMode';
import {
    isSketchStateObject as isUnifiedSelectionStateObject,
    pickEntityAt as pickUnifiedEntityAt,
    resolveContourLinkedSketchEntities as resolveUnifiedContourLinkedSketchEntities,
} from '../selection/EntitySelectionQueryHelpers';

//------ 装配体正式基准对象移入状态 开始------
/**
 * 正式基准点 / 轴命中类型枚举。
 */
export enum DatumReferencePriorityHitKind {
    /** 非正式基准点 / 轴对象。 */
    None = 'none',
    /** 正式基准轴对象。 */
    Axis = 'axis',
    /** 正式基准点对象。 */
    Point = 'point',
}
//------ 装配体正式基准对象移入状态 结束------

/**
 * 实体拖拽能力配置。
 */
export interface EntityDragCapabilityConfig {
    /** 是否启用点拖拽。 */
    pointDragEnabled: boolean;
    /** 是否启用线拖拽。 */
    lineDragEnabled: boolean;
    /** 是否启用轮廓拖拽。 */
    contourDragEnabled: boolean;
}

/** 默认实体拖拽能力配置。 */
const DEFAULT_ENTITY_DRAG_CAPABILITY_CONFIG: EntityDragCapabilityConfig = {
    pointDragEnabled: true,
    lineDragEnabled: true,
    contourDragEnabled: true,
};

//------ 草图框选几何包围盒 开始------
/**
 * 屏幕空间矩形。
 * 说明：框选逻辑只关心容器局部坐标，所有字段均为像素值。
 */
interface ScreenRect {
    /** 左侧 X 坐标。 */
    minX: number;
    /** 顶部 Y 坐标。 */
    minY: number;
    /** 右侧 X 坐标。 */
    maxX: number;
    /** 底部 Y 坐标。 */
    maxY: number;
}

/**
 * 草图点框选的屏幕半径。
 * 说明：点对象是 Sprite，框选语义应以点中心为准，同时保留少量像素容差匹配可视点大小。
 */
const SKETCH_POINT_BOX_SELECTION_SCREEN_HALF_SIZE = 4;
//------ 草图框选几何包围盒 结束------

/**
 * 实体选择处理器抽象基类。
 * 说明：
 * 1. 约束不同交互模式下的鼠标事件入口；
 * 2. 由具体模式处理类实现实际行为；
 * 3. EntitySelectionManager 只负责在不同处理器之间切换。
 */
export abstract class AbstractEntitySelectionHandler {
    /** 选择管理器。 */
    protected readonly entitySelectionManager: EntitySelectionManager;
    /** 射线投射器。 */
    protected readonly raycaster: THREE.Raycaster = new THREE.Raycaster();
    /** 标准化设备坐标。 */
    protected readonly mouseNdc: THREE.Vector2 = new THREE.Vector2();
    /** 拖拽能力配置。 */
    protected dragCapabilityConfig: EntityDragCapabilityConfig = {
        ...DEFAULT_ENTITY_DRAG_CAPABILITY_CONFIG,
    };

    //------ 框选屏幕包围盒调试 开始------
    /** 框选调试包围盒 DOM 元素集合。 */
    private readonly boxSelectionDebugElements: HTMLElement[] = [];
    //------ 框选屏幕包围盒调试 结束------

    /**
     * 构造函数。
     * @param entitySelectionManager 实体选择管理器。
     */
    protected constructor(entitySelectionManager: EntitySelectionManager) {
        this.entitySelectionManager = entitySelectionManager;
        this.raycaster.params.Line.threshold = 0.001
        this.raycaster.params.Points.threshold = 0.001
    }

    /**
     * 释放当前处理器拥有的临时资源。
     *
     * 管理器终止时会调用所有处理器，而非仅活动模式；子类应先释放自己的资源，再调用本实现。
     * 暂停事件桥接不调用此入口，以保留恢复交互所需的热态缓存。
     */
    public dispose(): void {
        this.clearBoxSelectionDebugObjectRects();
    }

    /**
     * 更新拖拽能力配置。
     * @param config 拖拽能力增量配置。
     */
    public setDragCapabilityConfig(config: Partial<EntityDragCapabilityConfig>): void {
        this.dragCapabilityConfig = {
            ...this.dragCapabilityConfig,
            ...config,
        };
    }

    /**
     * 获取当前拖拽能力配置快照。
     * @returns 拖拽能力配置。
     */
    public getDragCapabilityConfig(): Readonly<EntityDragCapabilityConfig> {
        return {
            ...this.dragCapabilityConfig,
        };
    }

    /**
     * 判断是否启用点拖拽。
     * @returns 是否启用点拖拽。
     */
    protected isPointDragEnabled(): boolean {
        return this.dragCapabilityConfig.pointDragEnabled;
    }

    /**
     * 判断是否启用线拖拽。
     * @returns 是否启用线拖拽。
     */
    protected isLineDragEnabled(): boolean {
        return this.dragCapabilityConfig.lineDragEnabled;
    }

    /**
     * 判断是否启用轮廓拖拽。
     * @returns 是否启用轮廓拖拽。
     */
    protected isContourDragEnabled(): boolean {
        return this.dragCapabilityConfig.contourDragEnabled;
    }

    /**
     * 判断当前模式是否允许框选。
     *
     * 设计说明：
     * 1. 默认返回 `true`，保持既有模式行为不变；
     * 2. 特定模式可通过覆写该方法禁用框选，而无需复制整套鼠标处理流程；
     * 3. 该钩子只控制框选启动与结算，不影响点击选择和 hover 语义。
     *
     * @returns 当前模式是否允许框选。
     */
    protected isBoxSelectionEnabled(): boolean {
        return true;
    }

    /**
     * 收集点击选择候选对象。
     * @returns 候选对象列表。
     */
    protected collectClickSelectionTargets(): THREE.Object3D[] {
        return this.entitySelectionManager.collectPointingSelectionTargets();
    }

    /**
     * 收集移入候选对象。
     * @returns 候选对象列表。
     */
    protected collectHoverSelectionTargets(): THREE.Object3D[] {
        return this.entitySelectionManager.collectPointingSelectionTargets();
    }

    /**
     * 收集当前可见基准面网格。
     *
     * 设计说明：
     * 1. 基准面不属于普通场景实体选择链，需由命令模式显式决定是否参与；
     * 2. 这里只提供受保护能力，避免所有模式默认都能选中基准面；
     * 3. 仅返回可见基准面，防止隐藏平面误参与拾取。
     *
     * @returns 可见基准面网格列表。
     */
    protected collectVisibleDatumPlaneTargets(): THREE.Object3D[] {
        return this.entitySelectionManager.renderer.getAllDatumPlanes()
            .filter((datumPlane) => datumPlane.isVisible())
            .map((datumPlane) => datumPlane.getObject3D());
    }

    //------ 装配体正式基准对象移入状态 开始------
    /**
     * 合并基础选择候选与正式基准对象候选。
     *
     * 设计说明：
     * 1. 正式基准面 / 轴 / 点不是统一实体选择遍历中的普通对象，需要由具体模式显式接入；
     * 2. 该方法只做候选收集，不改变命中优先级，具体优先级由拾取方法统一裁决；
     * 3. 子类按需调用该方法即可复用正式基准对象候选规则，避免在各选择处理器重复实现。
     *
     * @param baseTargets 基础候选对象。
     * @returns 去重后的候选对象。
     */
    protected collectDatumReferenceInteractionTargets(baseTargets: THREE.Object3D[]): THREE.Object3D[] {
        const collectedTargets: THREE.Object3D[] = [];
        const seenObjectIds = new Set<string>();

        // 先保留调用方已有候选，确保该方法只扩展正式基准对象能力，不重排原始候选。
        baseTargets.forEach((candidate) => {
            this.pushUniqueDatumReferenceObject(collectedTargets, seenObjectIds, candidate);
        });

        // 正式基准面以 group 作为唯一语义对象，后续子对象命中会统一归一化回该 group。
        this.entitySelectionManager.renderer.getAllDatumPlanes()
            .filter((datumPlane) => datumPlane.isVisible())
            .forEach((datumPlane) => {
                this.pushUniqueDatumReferenceObject(collectedTargets, seenObjectIds, datumPlane.getObject3D());
            });

        // 正式基准轴直接以可视线对象参与拾取，避免额外创建代理对象。
        this.entitySelectionManager.renderer.datumAxisManager?.getAllAxes()
            .filter((datumAxis) => datumAxis.isVisible())
            .forEach((datumAxis) => {
                this.pushUniqueDatumReferenceObject(collectedTargets, seenObjectIds, datumAxis);
            });

        // 正式基准点直接以 Sprite 参与拾取，保持点对象与标签、引用记录的稳定关系。
        this.entitySelectionManager.renderer.datumPointManager?.getAllPoints()
            .filter((datumPoint) => datumPoint.isVisible())
            .forEach((datumPoint) => {
                this.pushUniqueDatumReferenceObject(collectedTargets, seenObjectIds, datumPoint);
            });

        return collectedTargets;
    }

    //------ 基准参考元素屏幕空间像素拾取 开始------
    /**
     * 拾取正式基准对象。
     *
     * 设计说明：
     * 1. 基准点、基准轴以及基准面的边框改用屏幕空间像素拾取，提供高容差选择；
     * 2. 基准面的面片仍使用常规 3D 射线检测；
     * 3. 所有命中统一进行归一化并按照 点/轴优先 -> 面边框次之 -> 面片兜底 的优先级排序。
     *
     * @param clientX 鼠标屏幕 X 坐标。
     * @param clientY 鼠标屏幕 Y 坐标。
     * @param interactableObjects 当前候选交互对象列表。
     * @returns 命中结果；未命中时返回 null。
     */
    protected pickDatumReferenceAt(
        clientX: number,
        clientY: number,
        interactableObjects: THREE.Object3D[],
    ): THREE.Intersection<THREE.Object3D> | null {
        const container = this.entitySelectionManager.renderer.container;
        const camera = this.entitySelectionManager.renderer.camera;
        if (!container || !camera) {
            return null;
        }

        // 重新计算射线与屏幕位置
        const rect = container.getBoundingClientRect();
        this.mouseNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this.mouseNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
        this.raycaster.setFromCamera(this.mouseNdc, camera);

        // 筛选出参与拾取的正式基准参考对象
        const datumTargets = interactableObjects.filter((object) => this.isDatumReferenceInteractionTarget(object));
        if (datumTargets.length === 0) {
            return null;
        }

        // 存放不同几何分类以进行特定的拾取算法
        const pointsToPick: THREE.Sprite[] = [];
        const linesToPick: THREE.Line[] = [];
        const meshesToPick: THREE.Mesh[] = [];

        // 遍历基准候选，并将它们细分子对象归类至对应的拾取队列
        datumTargets.forEach((target) => {
            if (target instanceof THREE.Sprite) {
                // 基准点为 Sprite 对象
                pointsToPick.push(target);
            } else if (target instanceof THREE.Line) {
                // 基准轴为 Line 对象
                linesToPick.push(target);
            } else {
                // 基准面 group，遍历获取面片 Mesh 与边线 LineSegments
                target.traverse((child) => {
                    if (child instanceof THREE.Sprite) {
                        pointsToPick.push(child);
                    } else if (child instanceof THREE.Line) {
                        linesToPick.push(child);
                    } else if (child instanceof THREE.Mesh) {
                        meshesToPick.push(child);
                    }
                });
            }
        });

        // 1. 点的屏幕空间精确拾取
        const pointHits = this.entitySelectionManager.screenSpacePrecisionPicker.collectPointHits({
            points: pointsToPick,
            camera,
            containerRect: rect,
            clientX,
            clientY,
            ray: this.raycaster.ray,
        });
        // 2. 线（含基准轴与基准面边框）的屏幕空间精确拾取
        const lineHits = this.entitySelectionManager.screenSpacePrecisionPicker.collectLineHits({
            lines: linesToPick,
            camera,
            containerRect: rect,
            clientX,
            clientY,
            ray: this.raycaster.ray,
        });

        // 3. 面片常规三维射线投射检测
        const renderer = this.entitySelectionManager.renderer;
        const meshHits = renderer.intersectMeshRaycastCandidates
            ? renderer.intersectMeshRaycastCandidates(meshesToPick, this.raycaster, this, 'datum')
            : this.raycaster.intersectObjects(meshesToPick, false);

        // 统一归一化命中对象并过滤非法对象
        const validPointHits = pointHits
            .map((hit) => this.normalizeDatumReferenceIntersectionObject(hit))
            .filter((item): item is THREE.Intersection<THREE.Object3D> => (
                this.isDatumReferenceInteractionTarget(item.object)
            ));

        const validLineHits = lineHits
            .map((hit) => this.normalizeDatumReferenceIntersectionObject(hit))
            .filter((item): item is THREE.Intersection<THREE.Object3D> => (
                this.isDatumReferenceInteractionTarget(item.object)
            ));

        const validMeshHits = meshHits
            .map((hit) => this.normalizeDatumReferenceIntersectionObject(hit))
            .filter((item): item is THREE.Intersection<THREE.Object3D> => (
                this.isDatumReferenceInteractionTarget(item.object)
            ));

        // 优先级第一档：基准点与基准轴（因为较细小且易被面遮挡，屏幕拾取命中时最优先返回）
        const pointOrAxisHits = [...validPointHits, ...validLineHits].filter((hit) => {
            const kind = this.resolveDatumPointOrAxisHitKind(hit.object);
            return kind !== DatumReferencePriorityHitKind.None;
        });
        if (pointOrAxisHits.length > 0) {
            return pointOrAxisHits[0];
        }

        // 优先级第二档：基准面的边缘线（贴近边界时选边/面框）
        const planeEdgeHits = validLineHits.filter((hit) => {
            const kind = this.resolveDatumPointOrAxisHitKind(hit.object);
            return kind === DatumReferencePriorityHitKind.None;
        });
        if (planeEdgeHits.length > 0) {
            return planeEdgeHits[0];
        }

        // 优先级第三档：基准面片兜底
        return validMeshHits[0] ?? null;
    }
    //------ 基准参考元素屏幕空间像素拾取 结束------

    /**
     * 判断对象是否为正式基准点 / 轴命中。
     *
     * @param object 待判断对象。
     * @returns 命中类型。
     */
    protected resolveDatumPointOrAxisHitKind(object: THREE.Object3D): DatumReferencePriorityHitKind {
        if (this.entitySelectionManager.renderer.datumPointManager?.getAllPoints().some((datumPoint) => datumPoint === object)) {
            return DatumReferencePriorityHitKind.Point;
        }

        if (this.entitySelectionManager.renderer.datumAxisManager?.getAllAxes().some((datumAxis) => datumAxis === object)) {
            return DatumReferencePriorityHitKind.Axis;
        }

        return DatumReferencePriorityHitKind.None;
    }

    /**
     * 去重加入正式基准对象候选。
     *
     * @param bucket 目标列表。
     * @param seen 已记录对象集合。
     * @param object 候选对象。
     */
    private pushUniqueDatumReferenceObject(
        bucket: THREE.Object3D[],
        seen: Set<string>,
        object: THREE.Object3D | null | undefined,
    ): void {
        if (!object) {
            return;
        }
        // 多个来源可能同时带入同一对象，这里按 uuid 去重以保持拾取成本稳定。
        if (seen.has(object.uuid)) {
            return;
        }
        seen.add(object.uuid);
        bucket.push(object);
    }

    /**
     * 判断对象是否属于正式基准对象交互集合。
     *
     * @param object 待判断对象。
     * @returns 是否允许参与正式基准对象拾取。
     */
    private isDatumReferenceInteractionTarget(object: THREE.Object3D): boolean {
        // 基准面由 DatumPlane 管理，选择层只认其稳定 group 对象。
        if (this.entitySelectionManager.renderer.getAllDatumPlanes().some((datumPlane) => datumPlane.getObject3D() === object)) {
            return true;
        }

        // 基准轴和基准点由各自管理器保存，直接按对象实例判断能避免误识别普通线点。
        if (this.entitySelectionManager.renderer.datumAxisManager?.getAllAxes().some((datumAxis) => datumAxis === object)) {
            return true;
        }

        if (this.entitySelectionManager.renderer.datumPointManager?.getAllPoints().some((datumPoint) => datumPoint === object)) {
            return true;
        }

        return false;
    }

    /**
     * 归一化正式基准对象命中。
     *
     * @param intersection 原始命中。
     * @returns 归一化后的命中。
     */
    protected normalizeDatumReferenceIntersectionObject(
        intersection: THREE.Intersection<THREE.Object3D>,
    ): THREE.Intersection<THREE.Object3D> {
        // 基准面 mesh、边框与标签都是 group 子对象，hover / selected 必须回写到根 group 才能复用视觉状态。
        const matchedDatumPlane = this.entitySelectionManager.renderer.getAllDatumPlanes()
            .find((datumPlane) => {
                const planeGroup = datumPlane.getObject3D();
                return intersection.object === planeGroup || this.isAncestorOf(planeGroup, intersection.object);
            });
        if (!matchedDatumPlane) {
            return intersection;
        }

        return {
            ...intersection,
            object: matchedDatumPlane.getObject3D(),
        };
    }

    /**
     * 判断候选对象是否位于指定父对象层级内。
     *
     * @param ancestor 目标父对象。
     * @param candidate 候选对象。
     * @returns 候选对象属于目标父对象子树时返回 `true`。
     */
    private isAncestorOf(ancestor: THREE.Object3D, candidate: THREE.Object3D): boolean {
        let current: THREE.Object3D | null = candidate;
        while (current) {
            if (current === ancestor) {
                return true;
            }
            current = current.parent;
        }
        return false;
    }

    /**
     * 解析应优先返回的正式基准点 / 轴命中。
     *
     * @param hits 归一化命中结果列表。
     * @returns 需要优先返回的正式基准点 / 轴命中；不存在时返回 `null`。
     */
    private resolvePreferredDatumPointOrAxisHit(
        hits: Array<THREE.Intersection<THREE.Object3D>>,
    ): THREE.Intersection<THREE.Object3D> | null {
        let bestDatumPointHit: THREE.Intersection<THREE.Object3D> | null = null;
        let bestDatumAxisHit: THREE.Intersection<THREE.Object3D> | null = null;

        // 保持射线返回顺序，只提升第一条正式基准点 / 轴命中，避免重新排序带来不可预期选择变化。
        hits.forEach((hit) => {
            const datumHitKind = this.resolveDatumPointOrAxisHitKind(hit.object);
            if (datumHitKind === DatumReferencePriorityHitKind.Point && !bestDatumPointHit) {
                bestDatumPointHit = hit;
                return;
            }
            if (datumHitKind === DatumReferencePriorityHitKind.Axis && !bestDatumAxisHit) {
                bestDatumAxisHit = hit;
            }
        });

        return bestDatumPointHit ?? bestDatumAxisHit ?? null;
    }
    //------ 装配体正式基准对象移入状态 结束------

    /**
     * 根据屏幕坐标拾取实体。
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @param interactableObjects 可参与拾取的候选对象列表。
     * @returns 命中实体；未命中返回 null。
     */
    protected pickEntityAt(
        clientX: number,
        clientY: number,
        interactableObjects: THREE.Object3D[],
    ): THREE.Intersection<THREE.Object3D> | null {
        return pickUnifiedEntityAt(
            this.entitySelectionManager as unknown as EntitySelectionManagerRuntime,
            clientX,
            clientY,
            interactableObjects,
        );
    }

    /**
     * 以命令声明的临时拓扑模式执行统一实体拾取，不修改全局选择状态。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @param interactableObjects 可参与拾取的候选对象。
     * @param selectionMode 命令本轮需要的拓扑模式。
     * @returns 命中实体；未命中返回 null。
     */
    protected pickEntityAtForSelectionMode(
        clientX: number,
        clientY: number,
        interactableObjects: THREE.Object3D[],
        selectionMode: EntitySelectionMode,
    ): THREE.Intersection<THREE.Object3D> | null {
        return pickUnifiedEntityAt(
            this.entitySelectionManager as unknown as EntitySelectionManagerRuntime,
            clientX,
            clientY,
            interactableObjects,
            selectionMode,
        );
    }

    /**
     * 解析点击选择命中结果。
     * @param picked 原始命中结果。
     * @returns 点击选择应使用的命中结果。
     */
    protected resolveClickSelectionPick(
        picked: THREE.Intersection<THREE.Object3D> | null,
    ): THREE.Intersection<THREE.Object3D> | null {
        if (!picked) {
            return null;
        }

        const hitObject = picked.object;
        if (!isSketchLineObject(hitObject) && !isSketchArcObject(hitObject)) {
            return picked;
        }

        const slotOwner = this.findAncestorByGuard(hitObject, isSketchSlotObject);
        if (!slotOwner) {
            return picked;
        }

        return {
            ...picked,
            object: slotOwner,
        };
    }

    /**
     * 测量叠加层射线拾取入口。
     *
     * 说明：对外暴露受保护的 `pickEntityAt`，使测量处理器可复用各模式 Handler 的拾取扩展
     * （如 imported-model 代理解析、基准点/轴优先级）。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @param interactableObjects 可参与拾取的候选对象列表。
     * @returns 命中实体；未命中返回 null。
     */
    public pickMeasureEntityAt(
        clientX: number,
        clientY: number,
        interactableObjects: THREE.Object3D[],
    ): THREE.Intersection<THREE.Object3D> | null {
        return this.pickEntityAt(clientX, clientY, interactableObjects);
    }

    /**
     * 测量叠加层点击命中解析入口。
     *
     * @param picked 原始命中结果。
     * @returns 点击选择应使用的命中结果。
     */
    public resolveMeasureClickPick(
        picked: THREE.Intersection<THREE.Object3D> | null,
    ): THREE.Intersection<THREE.Object3D> | null {
        return this.resolveClickSelectionPick(picked);
    }

    //------ 选择悬浮状态下沉 开始------
    /**
     * 将纯悬停查询交给选择领域帧队列，原始事件及拖拽检查已经完成。
     * @param clientX 最新客户端 X 坐标。
     * @param clientY 最新客户端 Y 坐标。
     */
    protected queueHoverInput(clientX: number, clientY: number): void {
        this.entitySelectionManager.queueHoverInput(this, clientX, clientY);
    }

    /**
     * 消费悬停坐标；子类通过查询入口或本方法扩展专属候选/后置过滤。
     * @param clientX 客户端 X 坐标。
     * @param clientY 客户端 Y 坐标。
     */
    public consumeHoverInput(clientX: number, clientY: number): void {
        const pick = this.pickEntityAt(clientX, clientY, this.collectHoverSelectionTargets());
        this.updateHoverState(this.resolveHoverPickWithSelectionExclusion(pick));
    }

    /**
     * 更新 hover 状态并派发 enter/leave。
     * @param nextHoveredPick 新 hover 命中实体。
     */
    public updateHoverState(nextHoveredPick: THREE.Intersection<THREE.Object3D> | null): void {
        const currentHoveredPick = this.entitySelectionManager.getHoveredPick();
        if (this.isSamePick(currentHoveredPick, nextHoveredPick)) {
            return;
        }

        // 先派发 leave，保证同帧切换顺序稳定。
        if (currentHoveredPick) {
            this.entitySelectionManager.publishEntityHoverLeave(currentHoveredPick.object);
        }

        // 更新当前 hover 状态。
        this.entitySelectionManager.setHoveredPick(nextHoveredPick);

        // 再派发 enter。
        if (nextHoveredPick) {
            if (this.entitySelectionManager.isHoverDebugEnabled()) {
                console.log('[选择管理器] 移入对象:', [nextHoveredPick.object]);
            }
            this.entitySelectionManager.publishEntityHoverEnter(nextHoveredPick.object);
        }
    }

    /**
     * 清空 hover 状态。
     */
    public clearHoverState(): void {
        this.updateHoverState(null);
    }

    /**
     * 在计算 hover 时应用“选中与移入互斥”规则。
     * @param nextHoveredPick 新 hover 命中实体。
     * @returns 互斥规则处理后的 hover 命中实体。
     */
    public resolveHoverPickWithSelectionExclusion(
        nextHoveredPick: THREE.Intersection<THREE.Object3D> | null,
    ): THREE.Intersection<THREE.Object3D> | null {
        if (!nextHoveredPick) {
            return null;
        }
        if (this.isHoverMutuallyExclusiveWithSelection(nextHoveredPick)) {
            return null;
        }
        return nextHoveredPick;
    }

    /**
     * 判断 hover 是否与选中状态互斥。
     * 当前规则对线对象和样条对象生效：已选中的对象不再参与移入派发。
     * @param pick 目标命中实体。
     * @returns 是否互斥。
     */
    private isHoverMutuallyExclusiveWithSelection(pick: THREE.Intersection<THREE.Object3D>): boolean {
        if (
            !(pick.object instanceof SketchLineObject)
            && !isSketchSplineObject(pick.object)
            && !isSketchStyleSplineObject(pick.object)
        ) {
            return false;
        }
        return this.entitySelectionManager.isObjectSelected(pick.object);
    }

    /**
     * 比较两个命中实体是否一致。
     * @param left 左命中实体。
     * @param right 右命中实体。
     * @returns 是否一致。
     */
    private isSamePick(
        left: THREE.Intersection<THREE.Object3D> | null,
        right: THREE.Intersection<THREE.Object3D> | null,
    ): boolean {
        if (left === right) {
            return true;
        }
        if (!left || !right) {
            return false;
        }
        return left.object.uuid === right.object.uuid;
    }
    //------ 选择悬浮状态下沉 结束------

    /**
     * 根据 Ctrl 按键状态生成下一次选中集合。
     * @param nextSelectedPicks 本次新命中的选中集合。
     * @param isCtrlPressed 当前 Ctrl 是否按下。
     * @returns 应写入状态的最终选中集合。
     */
    protected resolveNextSelectionState(
        nextSelectedPicks: THREE.Intersection<THREE.Object3D>[],
        isCtrlPressed: boolean,
    ): THREE.Intersection<THREE.Object3D>[] {
        if (isCtrlPressed) {
            const toggledObjectIds = new Set(nextSelectedPicks.map((item) => item.object.uuid));
            const selectedPickMap = new Map<string, THREE.Intersection<THREE.Object3D>>();
            this.entitySelectionManager.getSelectedObjects().forEach((object) => {
                selectedPickMap.set(object.uuid, {
                    distance: 0,
                    object,
                    point: object.getWorldPosition(new THREE.Vector3()),
                });
            });

            const nextPrimaryPicks: THREE.Intersection<THREE.Object3D>[] = [];
            nextSelectedPicks.forEach((item) => {
                if (!selectedPickMap.has(item.object.uuid)) {
                    nextPrimaryPicks.push(item);
                }
            });

            const retainedSelectedPicks = this.entitySelectionManager.getSelectedObjects()
                .filter((object) => !toggledObjectIds.has(object.uuid))
                .map((object) => ({
                    distance: 0,
                    object,
                    point: object.getWorldPosition(new THREE.Vector3()),
                }));
            return [...nextPrimaryPicks, ...retainedSelectedPicks];
        }

        return nextSelectedPicks;
    }

    /**
     * 将对象数组转换为 Intersection 数组。
     * @param objects 对象列表。
     * @returns Intersection 列表。
     */
    protected toIntersections(objects: THREE.Object3D[]): THREE.Intersection<THREE.Object3D>[] {
        return objects.map((object) => ({
            distance: 0,
            object,
            point: object.getWorldPosition(new THREE.Vector3()),
        }));
    }

    /**
     * 判断对象是否属于允许参与状态判断的草图对象。
     * @param object 待判断对象。
     * @returns 是否为允许的草图对象。
     */
    protected isSketchStateObject(
        object: THREE.Object3D,
    ): boolean {
        return isUnifiedSelectionStateObject(object);
    }

    /**
     * 向上查找满足守卫条件的父级对象。
     * @param object 起始对象。
     * @param guard 类型守卫。
     * @returns 命中的父级对象；未命中返回 null。
     */
    protected findAncestorByGuard<T extends THREE.Object3D>(
        object: THREE.Object3D,
        guard: (value: THREE.Object3D) => value is T,
    ): T | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (guard(current)) {
                return current;
            }
            current = current.parent;
        }
        return null;
    }

    //------ 移动实体混合边轮廓选择 开始------
    /**
     * 从轮廓对象解析其关联草图实体列表。
     *
     * @param contour 轮廓对象。
     * @returns 关联草图实体列表。
     */
    protected resolveContourLinkedSketchEntities(contour: SketchContour): THREE.Object3D[] {
        return resolveUnifiedContourLinkedSketchEntities(contour);
    }

    /**
     * 从轮廓对象解析其关联的线对象列表。
     *
     * @param contour 轮廓对象。
     * @returns 关联线对象列表。
     */
    protected resolveContourLinkedLines(contour: SketchContour): SketchLineObject[] {
        return this.resolveContourLinkedSketchEntities(contour)
            .filter((object): object is SketchLineObject => isSketchLineObject(object));
    }
    //------ 移动实体混合边轮廓选择 结束------

    //------ 框选流程下沉 开始------
    /**
     * 收集框选候选对象。
     * 说明：
     * 1. 不同模式对候选对象范围有不同约束；
     * 2. 由子类实现具体候选收集策略。
     * @returns 候选对象列表。
     */
    protected collectBoxSelectionTargets(): THREE.Object3D[] {
        return this.entitySelectionManager.collectBoxSelectionTargets();
    }

    /**
     * 归一化框选命中对象。
     *
     * 设计说明：
     * 1. 该钩子位于“屏幕矩形命中判定之后、选中状态写入之前”，
     *    因此子类既能沿用统一窗口/交叉判定（按候选对象自身几何判定），
     *    又能定制最终写入选中集的对象语义；
     * 2. 默认原样返回命中集合，保持各模式既有框选行为不变；
     * 3. 复合草图实体（如槽口）可在此把命中的组成边提升为整体对象——
     *    提升必须发生在命中判定之后，否则“整体包围盒必须完整落入选择框”会与
     *    “只框住实体一部分”的用户预期互相矛盾。
     *
     * @param objects 统一窗口/交叉判定后命中的对象集合。
     * @returns 归一化后的命中对象集合。
     */
    protected promoteBoxSelectionHitObjects(objects: THREE.Object3D[]): THREE.Object3D[] {
        return objects;
    }

    /**
     * 更新框选框可视化。
     * @param start 鼠标按下起点。
     * @param end 鼠标当前位置。
     */
    protected updateSelectionBoxVisual(start: THREE.Vector2, end: THREE.Vector2): void {
        const container = this.entitySelectionManager.renderer.container;
        if (!container) {
            return;
        }

        const rect = container.getBoundingClientRect();
        const startX = start.x - rect.left;
        const startY = start.y - rect.top;
        const endX = end.x - rect.left;
        const endY = end.y - rect.top;

        const minX = Math.min(startX, endX);
        const minY = Math.min(startY, endY);
        const width = Math.abs(endX - startX);
        const height = Math.abs(endY - startY);
        const isCrossing = endX < startX;

        let selectionBoxElement = this.entitySelectionManager.getSelectionBoxElement();
        if (!selectionBoxElement) {
            const box = document.createElement('div');
            box.style.position = 'absolute';
            box.style.pointerEvents = 'none';
            box.style.zIndex = '10';
            container.appendChild(box);
            selectionBoxElement = box;
            this.entitySelectionManager.setSelectionBoxElement(box);
        }

        const borderColor = isCrossing ? 'rgba(64, 196, 255, 0.95)' : 'rgba(255, 152, 0, 0.95)';
        const backgroundColor = isCrossing ? 'rgba(64, 196, 255, 0.12)' : 'rgba(255, 152, 0, 0.12)';

        selectionBoxElement.style.left = `${minX}px`;
        selectionBoxElement.style.top = `${minY}px`;
        selectionBoxElement.style.width = `${width}px`;
        selectionBoxElement.style.height = `${height}px`;
        selectionBoxElement.style.border = `1px solid ${borderColor}`;
        selectionBoxElement.style.background = backgroundColor;

        //------ 框选屏幕包围盒调试 开始------
        if (this.entitySelectionManager.isBoxSelectDebugEnabled()) {
            this.renderBoxSelectionDebugObjectRects(container, rect);
        } else {
            this.clearBoxSelectionDebugObjectRects();
        }
        //------ 框选屏幕包围盒调试 结束------
    }

    /**
     * 清理框选框可视化。
     */
    protected resetSelectionBoxVisual(): void {
        const selectionBoxElement = this.entitySelectionManager.getSelectionBoxElement();
        if (selectionBoxElement && selectionBoxElement.parentElement) {
            selectionBoxElement.parentElement.removeChild(selectionBoxElement);
        }
        this.entitySelectionManager.setSelectionBoxElement(null);
        //------ 框选屏幕包围盒调试 开始------
        this.clearBoxSelectionDebugObjectRects();
        //------ 框选屏幕包围盒调试 结束------
    }

    /**
     * 执行框选。
     * @param start 鼠标按下起点。
     * @param end 鼠标抬起终点。
     * @param isCtrlPressed 是否按下 Ctrl。
     */
    protected performBoxSelection(start: THREE.Vector2, end: THREE.Vector2, isCtrlPressed: boolean): void {
        const container = this.entitySelectionManager.renderer.container;
        const camera = this.entitySelectionManager.renderer.camera;
        if (!container || !camera) {
            return;
        }

        const containerRect = container.getBoundingClientRect();
        const startX = start.x - containerRect.left;
        const startY = start.y - containerRect.top;
        const endX = end.x - containerRect.left;
        const endY = end.y - containerRect.top;

        const selectionRect = {
            minX: Math.min(startX, endX),
            minY: Math.min(startY, endY),
            maxX: Math.max(startX, endX),
            maxY: Math.max(startY, endY),
        };
        const isCrossing = endX < startX;

        const selectedObjectsMap = new Map<string, THREE.Object3D>();
        const targets = this.collectBoxSelectionTargets();
        targets.forEach((target) => {
            if (!this.isSketchStateObject(target)) {
                return;
            }
            const objectRect = this.computeObjectScreenRect(target, camera, containerRect);
            if (!objectRect) {
                return;
            }
            const hit = isCrossing
                ? this.rectsIntersect(selectionRect, objectRect)
                : this.rectContains(selectionRect, objectRect);
            if (!hit) {
                return;
            }
            selectedObjectsMap.set(target.uuid, target);
        });

        const selectedObjects = this.promoteBoxSelectionHitObjects(Array.from(selectedObjectsMap.values()))
            .filter((object) => !isSketchCollectionObject(object));

        if (this.entitySelectionManager.isBoxSelectDebugEnabled()) {
            console.log('[选择管理器] 框选对象:', selectedObjects);
        }

        this.entitySelectionManager.updateSelectionState(
            this.resolveNextSelectionState(this.toIntersections(selectedObjects), isCtrlPressed),
        );
        this.publishBoxSelectionEvents(selectedObjects, isCrossing, selectionRect);
    }

    /**
     * 发布框选相关事件。
     * 说明：
     * 1. 默认同时发布 `EntityBoxSelected`；
     * 2. 当命中对象数量大于 1 时，再发布 `MultipleEntitiesBoxSelected`；
     * 3. 子类可按模式覆盖该方法，定制是否发布以及发布策略。
     * @param selectedObjects 本次框选命中的对象集合。
     * @param isCrossing 是否为 crossing 框选。
     * @param selectionRect 本次框选的屏幕矩形。
     */
    protected publishBoxSelectionEvents(
        selectedObjects: THREE.Object3D[],
        isCrossing: boolean,
        selectionRect: { minX: number; minY: number; maxX: number; maxY: number },
    ): void {
        this.entitySelectionManager.publishEntityBoxSelected({
            type: EntityInteractionEventType.EntityBoxSelected,
            selectedObjectIds: selectedObjects.map((item) => item.uuid),
            selectedObjects,
            isCrossing,
            selectionRect,
            timestamp: Date.now(),
        });

        if (selectedObjects.length > 1) {
            this.entitySelectionManager.publishMultipleEntitiesBoxSelected(selectedObjects, isCrossing);
        }
    }

    /**
     * 计算对象屏幕包围盒。
     * @param object 目标对象。
     * @param camera 当前相机。
     * @param containerRect 容器包围盒。
     * @returns 屏幕包围盒。
     */
    private computeObjectScreenRect(
        object: THREE.Object3D,
        camera: THREE.Camera,
        containerRect: DOMRect,
    ): ScreenRect | null {
        //------ 草图框选几何包围盒 开始------
        // 草图点和草图边必须按自身几何计算，避免 Object3D 子树中的控制点、集合节点放大包围盒。
        if (isSketchPointObject(object)) {
            return this.computeSketchPointScreenRect(object, camera, containerRect);
        }
        if (isAbstractSketchEdgeObject(object)) {
            return this.computeSketchEdgeScreenRect(object, camera, containerRect);
        }
        //------ 草图框选几何包围盒 结束------

        return this.computeGenericObjectScreenRect(object, camera, containerRect);
    }

    //------ 草图框选几何包围盒 开始------
    /**
     * 计算草图点对象的屏幕包围盒。
     *
     * @param object 草图点对象。
     * @param camera 当前相机。
     * @param containerRect 容器包围盒。
     * @returns 草图点屏幕包围盒；投影无效时返回 null。
     */
    private computeSketchPointScreenRect(
        object: SketchPointObject,
        camera: THREE.Camera,
        containerRect: DOMRect,
    ): ScreenRect | null {
        const screenPoint = this.projectWorldPointToScreenPoint(object.getPosition(), camera, containerRect);
        if (!screenPoint) {
            return null;
        }

        const halfSize = SKETCH_POINT_BOX_SELECTION_SCREEN_HALF_SIZE;
        return {
            minX: screenPoint.x - halfSize,
            minY: screenPoint.y - halfSize,
            maxX: screenPoint.x + halfSize,
            maxY: screenPoint.y + halfSize,
        };
    }

    /**
     * 计算草图边对象的屏幕包围盒。
     *
     * 设计说明：
     * 1. 草图边的 `geometry.position` 表示该边自身的曲线采样点；
     * 2. 直接投影这些采样点可以得到线、圆、圆弧、椭圆、样条等实体的真实屏幕范围；
     * 3. 禁止使用 `Box3.setFromObject`，否则会把端点、中点、控制点或闭合集合子树一起算入框选语义。
     *
     * @param object 草图边对象。
     * @param camera 当前相机。
     * @param containerRect 容器包围盒。
     * @returns 草图边屏幕包围盒；几何无效时返回 null。
     */
    private computeSketchEdgeScreenRect(
        object: AbstractSketchEdgeObject,
        camera: THREE.Camera,
        containerRect: DOMRect,
    ): ScreenRect | null {
        const positionAttribute = object.geometry.getAttribute('position');
        if (!positionAttribute || positionAttribute.count === 0) {
            return null;
        }

        const screenRect = this.createEmptyScreenRect();
        const worldPoint = new THREE.Vector3();
        for (let index = 0; index < positionAttribute.count; index += 1) {
            // 当前草图实体的顶点已经是草图世界点，继续套用 Object3D 层级矩阵会重复变换。
            worldPoint.fromBufferAttribute(positionAttribute, index);
            this.expandScreenRectByWorldPoint(screenRect, worldPoint, camera, containerRect);
        }

        return this.normalizeScreenRect(screenRect);
    }

    /**
     * 计算通用 Three 对象的屏幕包围盒。
     *
     * @param object 目标对象。
     * @param camera 当前相机。
     * @param containerRect 容器包围盒。
     * @returns 屏幕包围盒；对象包围盒为空时返回 null。
     */
    private computeGenericObjectScreenRect(
        object: THREE.Object3D,
        camera: THREE.Camera,
        containerRect: DOMRect,
    ): ScreenRect | null {
        const worldBox = new THREE.Box3().setFromObject(object);
        if (worldBox.isEmpty()) {
            return null;
        }

        const cornerPoints: THREE.Vector3[] = [
            new THREE.Vector3(worldBox.min.x, worldBox.min.y, worldBox.min.z),
            new THREE.Vector3(worldBox.min.x, worldBox.min.y, worldBox.max.z),
            new THREE.Vector3(worldBox.min.x, worldBox.max.y, worldBox.min.z),
            new THREE.Vector3(worldBox.min.x, worldBox.max.y, worldBox.max.z),
            new THREE.Vector3(worldBox.max.x, worldBox.min.y, worldBox.min.z),
            new THREE.Vector3(worldBox.max.x, worldBox.min.y, worldBox.max.z),
            new THREE.Vector3(worldBox.max.x, worldBox.max.y, worldBox.min.z),
            new THREE.Vector3(worldBox.max.x, worldBox.max.y, worldBox.max.z),
        ];

        const screenRect = this.createEmptyScreenRect();
        cornerPoints.forEach((point) => {
            this.expandScreenRectByWorldPoint(screenRect, point, camera, containerRect);
        });

        return this.normalizeScreenRect(screenRect);
    }

    /**
     * 创建空屏幕矩形。
     *
     * @returns 可用于后续扩展的屏幕矩形。
     */
    private createEmptyScreenRect(): ScreenRect {
        return {
            minX: Number.POSITIVE_INFINITY,
            minY: Number.POSITIVE_INFINITY,
            maxX: Number.NEGATIVE_INFINITY,
            maxY: Number.NEGATIVE_INFINITY,
        };
    }

    /**
     * 将世界点投影到容器局部屏幕坐标。
     *
     * @param worldPoint 世界坐标点。
     * @param camera 当前相机。
     * @param containerRect 容器包围盒。
     * @returns 容器局部屏幕坐标；投影无效时返回 null。
     */
    private projectWorldPointToScreenPoint(
        worldPoint: THREE.Vector3,
        camera: THREE.Camera,
        containerRect: DOMRect,
    ): { x: number; y: number } | null {
        const projected = worldPoint.clone().project(camera);
        const x = ((projected.x + 1) * 0.5) * containerRect.width;
        const y = ((1 - projected.y) * 0.5) * containerRect.height;
        if (!Number.isFinite(x) || !Number.isFinite(y)) {
            return null;
        }
        return { x, y };
    }

    /**
     * 使用世界点扩展屏幕矩形。
     *
     * @param screenRect 待扩展屏幕矩形。
     * @param worldPoint 世界坐标点。
     * @param camera 当前相机。
     * @param containerRect 容器包围盒。
     */
    private expandScreenRectByWorldPoint(
        screenRect: ScreenRect,
        worldPoint: THREE.Vector3,
        camera: THREE.Camera,
        containerRect: DOMRect,
    ): void {
        const screenPoint = this.projectWorldPointToScreenPoint(worldPoint, camera, containerRect);
        if (!screenPoint) {
            return;
        }

        screenRect.minX = Math.min(screenRect.minX, screenPoint.x);
        screenRect.minY = Math.min(screenRect.minY, screenPoint.y);
        screenRect.maxX = Math.max(screenRect.maxX, screenPoint.x);
        screenRect.maxY = Math.max(screenRect.maxY, screenPoint.y);
    }

    /**
     * 归一化屏幕矩形。
     *
     * @param screenRect 待校验屏幕矩形。
     * @returns 有效屏幕矩形；任一边界无效时返回 null。
     */
    private normalizeScreenRect(screenRect: ScreenRect): ScreenRect | null {
        if (
            !Number.isFinite(screenRect.minX)
            || !Number.isFinite(screenRect.minY)
            || !Number.isFinite(screenRect.maxX)
            || !Number.isFinite(screenRect.maxY)
        ) {
            return null;
        }

        return screenRect;
    }
    //------ 草图框选几何包围盒 结束------

    //------ 框选屏幕包围盒调试 开始------
    /**
     * 渲染框选候选对象的屏幕包围盒调试层。
     *
     * @param container 视口容器。
     * @param containerRect 视口容器屏幕矩形。
     */
    private renderBoxSelectionDebugObjectRects(container: HTMLElement, containerRect: DOMRect): void {
        const camera = this.entitySelectionManager.renderer.camera;
        if (!camera) {
            return;
        }

        this.clearBoxSelectionDebugObjectRects();

        const targets = this.collectBoxSelectionTargets();
        targets.forEach((target) => {
            if (!this.isSketchStateObject(target)) {
                return;
            }

            const objectRect = this.computeObjectScreenRect(target, camera, containerRect);
            if (!objectRect) {
                return;
            }

            const width = objectRect.maxX - objectRect.minX;
            const height = objectRect.maxY - objectRect.minY;
            const box = document.createElement('div');
            const label = document.createElement('div');

            box.style.position = 'absolute';
            box.style.pointerEvents = 'none';
            box.style.zIndex = '11';
            box.style.left = `${objectRect.minX}px`;
            box.style.top = `${objectRect.minY}px`;
            box.style.width = `${width}px`;
            box.style.height = `${height}px`;
            box.style.border = '1px dashed rgba(0, 180, 90, 0.95)';
            box.style.background = 'rgba(0, 180, 90, 0.08)';
            box.style.boxSizing = 'border-box';

            label.textContent = `${target.name || target.type}: ${width.toFixed(1)} x ${height.toFixed(1)}`;
            label.style.position = 'absolute';
            label.style.left = '0';
            label.style.top = '-18px';
            label.style.padding = '1px 4px';
            label.style.fontSize = '10px';
            label.style.lineHeight = '14px';
            label.style.color = '#fff';
            label.style.background = 'rgba(0, 120, 60, 0.9)';
            label.style.whiteSpace = 'nowrap';
            label.style.pointerEvents = 'none';
            box.appendChild(label);

            container.appendChild(box);
            this.boxSelectionDebugElements.push(box);
        });
    }

    /**
     * 清理框选候选对象屏幕包围盒调试层。
     */
    private clearBoxSelectionDebugObjectRects(): void {
        this.boxSelectionDebugElements.forEach((element) => {
            element.parentElement?.removeChild(element);
        });
        this.boxSelectionDebugElements.length = 0;
    }
    //------ 框选屏幕包围盒调试 结束------

    /**
     * 判断外矩形是否包含内矩形。
     * @param outer 外矩形。
     * @param inner 内矩形。
     * @returns 是否包含。
     */
    private rectContains(
        outer: { minX: number; minY: number; maxX: number; maxY: number },
        inner: { minX: number; minY: number; maxX: number; maxY: number },
    ): boolean {
        return outer.minX <= inner.minX
            && outer.minY <= inner.minY
            && outer.maxX >= inner.maxX
            && outer.maxY >= inner.maxY;
    }

    /**
     * 判断两个矩形是否相交。
     * @param left 矩形 A。
     * @param right 矩形 B。
     * @returns 是否相交。
     */
    private rectsIntersect(
        left: { minX: number; minY: number; maxX: number; maxY: number },
        right: { minX: number; minY: number; maxX: number; maxY: number },
    ): boolean {
        return !(
            left.maxX < right.minX
            || left.minX > right.maxX
            || left.maxY < right.minY
            || left.minY > right.maxY
        );
    }
    //------ 框选流程下沉 结束------

    /**
     * 处理鼠标按下。
     * @param payload 原始事件。
     */
    public abstract onMouseDown(payload: RawPointerEventPayload): void;

    /**
     * 处理鼠标移动。
     * @param payload 原始事件。
     */
    public abstract onMouseMove(payload: RawPointerEventPayload): void;

    /**
     * 处理鼠标抬起。
     * @param payload 原始事件。
     */
    public abstract onMouseUp(payload: RawPointerEventPayload): void;

    /**
     * 处理鼠标会话取消。
     *
     * 设计说明：
     * canvas 离开、窗口释放或窗口失焦都不是业务层正常 click，但它们必须复用 mouseup 的状态清理能力。
     *
     * @param payload 原始事件。
     */
    public onPointerCancel(payload: RawPointerEventPayload): void {
        this.onMouseUp(payload);
    }

    /**
     * 处理鼠标点击。
     * @param payload 原始事件。
     */
    public abstract onClick(payload: RawPointerEventPayload): void;
}
